import "components/QuerySection/components/OCRStatusList.scss";

import Button from "components/Button/Button";
import OCRMSADecisionModal from "components/Modals/OCRMSADecisionModal";
import { useMemo, useState } from "react";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import { useGetMSADecisionsQuery } from "services/msa-api";
import { useGetTextractRequestsByFileQuery } from "services/rre-profile-ingestion-api";
import type {
  BackgroundJobDataType,
  MSADecisionReviewPayload,
  MSADecisionType,
  PaginatedResponse,
} from "types/DataTypes";

type MSADecisionOCRReviewProps = {
  msaId: string;
  fileId: number;
};

const isJobFailed = (job: BackgroundJobDataType) =>
  job.status === "Failed" || job.status === "Error";

// Tracks whichever undecided decision on this MSA most needs attention, and stays up once finished until the decision is actually saved.
const MSADecisionOCRReview = ({ msaId, fileId }: MSADecisionOCRReviewProps) => {
  const [dismissedJobId, setDismissedJobId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: decisionsResponse } = useGetMSADecisionsQuery({ msaId });
  const decisions = (
    decisionsResponse as unknown as PaginatedResponse<MSADecisionType>
  )?.results;
  // A new re-review's decision can be created before an older one is confirmed, so several undecided decisions can exist at once.
  const undecidedDecisions = decisions?.filter((d) => d.decision_type === null);

  const { data: jobsResponse } = useGetBackgroundJobsQuery(
    { filters: { msa_id: msaId } },
    { skip: !undecidedDecisions?.length },
  );

  // Prefers a decision with a non-failed job, then one with any job, then falls back to the newest.
  const trackedDecision = useMemo(() => {
    if (!undecidedDecisions?.length) return null;
    const jobFor = (decisionId: number) =>
      jobsResponse?.results?.find((j) => j.msa_decision_id === decisionId);
    return (
      undecidedDecisions.find((d) => {
        const job = jobFor(d.id);
        return job && !isJobFailed(job);
      }) ||
      undecidedDecisions.find((d) => jobFor(d.id)) ||
      undecidedDecisions[undecidedDecisions.length - 1]
    );
  }, [undecidedDecisions, jobsResponse]);

  const latestJob = useMemo(
    () =>
      jobsResponse?.results?.find(
        (j) => j.msa_decision_id === trackedDecision?.id,
      ) || null,
    [jobsResponse, trackedDecision],
  );

  const failed = !!latestJob && isJobFailed(latestJob);
  // Also finished on a bare "Success" with no payload, so a job that somehow finished without one never
  // polls forever instead of just showing an empty review.
  const isFinished =
    !!latestJob &&
    (failed || !!latestJob.review_payload || latestJob.status === "Success");

  // Scoped to msa_decision jobs only, since an unrelated Textract job on the same file could otherwise be mistaken for this job's own request. Keeps polling while unresolved so it sees the request created after submit.
  const { data: textractRequests } = useGetTextractRequestsByFileQuery(
    { fileId, jobType: "msa_decision" },
    { skip: !latestJob, pollingInterval: isFinished ? 0 : 3000 },
  );
  // Matched by this job's own textract_request_id, since a prior attempt's already-terminal request would otherwise make "Matching..." fire the moment this job is created.
  const ownTextractRequest = latestJob?.textract_request_id
    ? textractRequests?.find((r) => r.id === latestJob.textract_request_id)
    : null;
  const isDismissed = latestJob?.id === dismissedJobId;

  // This component owns the whole lifecycle (queued, processing, matching, review) as one state machine, since
  // splitting it across two separately polled queries let them disagree and show two messages at once.
  type Phase = "queued" | "processing" | "matching" | "review" | "failed";
  const phase: Phase = failed
    ? "failed"
    : latestJob?.review_payload
      ? "review"
      : !ownTextractRequest || ownTextractRequest.status === "pending"
        ? "queued"
        : ownTextractRequest.status === "in_progress"
          ? "processing"
          : "matching";
  const isActive =
    phase === "queued" || phase === "processing" || phase === "matching";

  // Polls as long as the job is unresolved, not just while already active, since the active phases are derived from this same data and gating on them would mean nothing ever refetches it.
  useGetBackgroundJobsQuery(
    { filters: { msa_id: msaId } },
    {
      skip: !undecidedDecisions?.length,
      pollingInterval: !isFinished ? 3000 : 0,
    },
  );

  const handleCloseModal = () => {
    // A ready-to-review job otherwise stays until Save actually clears decision_type, unless failed.
    if (latestJob && failed) {
      setDismissedJobId(latestJob.id);
    }
    setIsModalOpen(false);
  };

  if (!trackedDecision || !latestJob || isDismissed) {
    return null;
  }

  // Same modifier names OCRStatusList uses, so this row picks up its icon color, border, and pulse for free.
  const statusKey =
    phase === "failed" ? "failed" : isActive ? "in_progress" : "succeeded";
  const icon =
    phase === "failed"
      ? "error"
      : isActive
        ? "document_scanner"
        : "check_circle";
  const statusText: Record<Phase, string> = {
    queued: "Queued for OCR processing...",
    processing: "Processing OCR...",
    matching: "Matching the decision letter against your allocations...",
    failed:
      latestJob.status_message ||
      "Something went wrong processing this letter.",
    review:
      "The decision letter has been OCR-ed and matched against your allocations.",
  };

  return (
    <>
      {isModalOpen && (
        <OCRMSADecisionModal
          isOpen
          onClose={handleCloseModal}
          msaId={msaId}
          decisionId={trackedDecision.id}
          initialReviewPayload={
            latestJob?.review_payload as
              | MSADecisionReviewPayload
              | null
              | undefined
          }
        />
      )}
      <div className="ocr-status-list">
        <div className="data-block">
          <div className="data-block-header">
            <span className="material-icons ocr-status-list__header-icon">
              document_scanner
            </span>
            CMS Decision Letter OCR
          </div>
          <div className="ocr-status-list__items">
            <div className={`ocr-status-item ocr-status-item--${statusKey}`}>
              <div className="ocr-status-item__icon-wrapper">
                <span
                  className={`material-icons ocr-status-item__icon ${isActive ? "ocr-status-item__icon--pulse" : ""}`}
                >
                  {icon}
                </span>
              </div>
              <div className="ocr-status-item__content">
                <div className="ocr-status-item__status-text">
                  {statusText[phase]}
                </div>
              </div>
              {isFinished && (
                <Button onClick={() => setIsModalOpen(true)}>
                  {failed ? "Try Again" : "Review"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MSADecisionOCRReview;
